import { Fragment, useEffect, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { AUTH_MODES, automatedAnswerAudit, type ActorDTO, type AuthMode, type EventDTO, type Stage, type Tier } from "@roomate/core";

export const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

/** How an agent actually authenticates, defaulted/validated like the server's `authModeFor` (apps/server/src/agent/run-config.ts) — unset or unrecognized value falls back to "seat". */
export function authModeOf(actor: ActorDTO): AuthMode {
  const auth = actor.agentConfig?.auth;
  return auth && (AUTH_MODES as readonly string[]).includes(auth) ? auth : "seat";
}

const TIER_STYLE: Record<Tier, string> = {
  low: "text-low border-low/40 bg-low/10",
  medium: "text-medium border-medium/40 bg-medium/10",
  high: "text-high border-high/40 bg-high/10",
  critical: "text-paper bg-critical border-critical",
};

export function TierChip({ tier, className }: { tier: Tier; className?: string }) {
  return (
    <span className={cx("inline-flex items-center rounded-sm border px-1.5 font-mono text-[10.5px] uppercase tracking-wider", TIER_STYLE[tier], className)}>
      {tier}
    </span>
  );
}

export const STAGE_LABEL: Record<Stage, string> = {
  backlog: "Backlog",
  ready: "Ready",
  in_progress: "Building",
  verifying: "Verifying",
  review: "Human review",
  blocked: "Blocked",
  done: "Done",
  cancelled: "Cancelled",
};

export function Glyph({ kind, className }: { kind: ActorDTO["kind"]; className?: string }) {
  return (
    <span aria-hidden className={cx("inline-block w-3 text-center leading-none", kind === "agent" ? "text-ink-2" : "text-signal", className)}>
      {kind === "agent" ? "◆" : "●"}
    </span>
  );
}

export function ActorName({
  id,
  actors,
  fallback = "—",
  showOnline = false,
  showKind = false,
}: {
  id: string | null | undefined;
  actors: ActorDTO[] | undefined;
  fallback?: string;
  showOnline?: boolean;
  /** Spell out "human"/"agent" next to the glyph, so builder kind reads without hovering. */
  showKind?: boolean;
}) {
  const a = id ? actors?.find((x) => x.id === id) : undefined;
  if (!a) return <span className="text-ink-3">{id ? id.slice(0, 10) : fallback}</span>;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap" title={a.kind === "agent" ? `agent · ${a.runtime}` : a.role.replace("_", " ")}>
      <Glyph kind={a.kind} />
      {showKind && <span className="font-mono text-[10.5px] uppercase tracking-wider text-ink-3">{a.kind}</span>}
      <span>{a.name}</span>
      {showOnline && a.online && <span className="live-dot ml-0.5" title="online" />}
    </span>
  );
}

/** Current time, re-read every `intervalMs` so a live clock re-renders without a refetch. */
export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function Ago({ ts, now }: { ts: number; now?: number }) {
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    if (now) return;
    const t = setInterval(() => setTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [now]);
  return <time dateTime={new Date(ts).toISOString()} title={new Date(ts).toLocaleString()}>{formatAgo((now ?? tick) - ts)}</time>;
}

export function formatAgo(ms: number): string {
  const min = Math.max(0, Math.round(ms / 60_000));
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function formatMin(min: number | null | undefined): string {
  if (min === null || min === undefined) return "—";
  if (min < 60) return `${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Elapsed time for the lead-time clock. Floors, never rounds up; negative or NaN input (clock skew) reads as `<1m`. */
export function formatSpan(ms: number): string {
  if (!(ms >= 60_000)) return "<1m";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
  if (ms < 86_400_000) {
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(ms / 86_400_000);
  const h = Math.floor((ms % 86_400_000) / 3_600_000);
  return h ? `${d}d ${h}h` : `${d}d`;
}

export function Button({ variant = "plain", className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "plain" | "primary" | "danger" | "ghost" }) {
  return (
    <button
      {...props}
      className={cx(
        "inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        variant === "primary" && "bg-ink text-paper hover:bg-ink-2",
        variant === "plain" && "border border-rule bg-card hover:border-ink-3",
        variant === "danger" && "border border-high/50 text-high hover:bg-high/10",
        variant === "ghost" && "text-ink-2 hover:bg-paper-2",
        className,
      )}
    />
  );
}

export function Section({ title, aside, children, className }: { title: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={className}>
      <div className="mb-2 flex items-baseline justify-between gap-3 border-b border-rule pb-1.5">
        <h2 className="text-[13px] font-medium text-ink-2">{title}</h2>
        {aside && <div className="text-[12px] text-ink-3">{aside}</div>}
      </div>
      {children}
    </section>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-[13px] italic text-ink-3">{children}</p>;
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="rounded-md border border-high/40 bg-high/10 px-3 py-2 text-[13px] text-high">
      {error instanceof Error ? error.message : String(error)}
    </p>
  );
}

export const inputCls =
  "w-full rounded-md border border-rule bg-card px-2.5 py-1.5 text-[13px] placeholder:text-ink-3 focus:border-ink-3 focus:outline-none";

/** Tiny, safe Markdown: paragraphs, **bold**, `code`, fenced code blocks, lists and pipe tables. No raw HTML. */
export function Markdown({ text, className, headings = false }: { text: string; className?: string; /** Render `#` to `####` lines as headings; off by default so existing callers keep their text as it was. */ headings?: boolean }) {
  return (
    <div className={cx("md space-y-2 text-[13.5px] leading-relaxed", className)}>
      {splitFences(text.trim()).map((part, i) =>
        part.code === null ? (
          <MarkdownBlocks key={i} text={part.text} headings={headings} />
        ) : (
          <pre key={i} className="overflow-x-auto whitespace-pre rounded-xl bg-ink/[0.06] px-3.5 py-2.5 font-mono text-[12.5px] leading-relaxed text-ink">
            <code>{part.code}</code>
          </pre>
        ),
      )}
    </div>
  );
}

/**
 * Cuts the text at whole-line ``` fences. Text inside a fence is code, blank lines included, so it
 * must be cut out before the paragraph split. An unclosed fence runs to the end, as in CommonMark.
 */
export function splitFences(text: string): { code: string | null; text: string }[] {
  const out: { code: string | null; text: string }[] = [];
  let prose: string[] = [];
  let code: string[] | null = null;
  const flushProse = () => {
    if (prose.some((l) => l.trim())) out.push({ code: null, text: prose.join("\n") });
    prose = [];
  };
  for (const line of text.split("\n")) {
    if (code === null) {
      if (/^\s*```/.test(line)) {
        flushProse();
        code = [];
      } else prose.push(line);
    } else if (/^\s*```\s*$/.test(line)) {
      out.push({ code: code.join("\n"), text: "" });
      code = null;
    } else code.push(line);
  }
  if (code !== null) out.push({ code: code.join("\n"), text: "" });
  else flushProse();
  return out;
}

/** Cuts a block so every `#` heading line stands alone and the lines between headings stay together. */
function splitAtHeadings(block: string): string[] {
  const out: string[] = [];
  let rest: string[] = [];
  const flush = () => {
    if (rest.length) out.push(rest.join("\n"));
    rest = [];
  };
  for (const line of block.split("\n")) {
    if (/^#{1,4} /.test(line)) {
      flush();
      out.push(line);
    } else rest.push(line);
  }
  flush();
  return out;
}

function MarkdownBlocks({ text, headings }: { text: string; headings: boolean }) {
  // A heading line is its own block, so "## Title" directly above a list still renders as a heading and a list.
  const blocks = text.trim().split(/\n{2,}/).flatMap((b) => (headings ? splitAtHeadings(b) : [b]));
  return (
    <>
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        const heading = /^(#{1,4}) (.*)$/.exec(block);
        if (headings && lines.length === 1 && heading)
          return (
            <p key={i} className={heading[1]!.length === 1 ? "pt-1 text-[15px] font-semibold" : "pt-1 font-semibold"}>
              {inline(heading[2]!)}
            </p>
          );
        if (lines.every((l) => l.trim().startsWith("|"))) {
          const rows = lines.filter((l) => !/^\s*\|[\s|:-]+\|\s*$/.test(l)).map((l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim()));
          const [head, ...body] = rows;
          return (
            <div key={i} className="overflow-x-auto">
              <table>
                <thead>
                  <tr>{head?.map((c, j) => <th key={j}>{inline(c)}</th>)}</tr>
                </thead>
                <tbody>
                  {body.map((r, k) => (
                    <tr key={k}>{r.map((c, j) => <td key={j}>{inline(c)}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (lines.every((l) => /^\s*[-*] /.test(l))) {
          return (
            <ul key={i} className="list-disc space-y-0.5 pl-5">
              {lines.map((l, j) => (
                <li key={j}>{inline(l.replace(/^\s*[-*] /, ""))}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i}>
            {lines.map((l, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                {inline(l)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    out.push(tok.startsWith("**") ? <strong key={m.index}>{tok.slice(2, -2)}</strong> : <code key={m.index}>{tok.slice(1, -1)}</code>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** One-line sentence for an event, for feeds and timelines. */
export function describeEvent(e: EventDTO, name: (id: string | null | undefined) => string): string {
  const p = e.payload as Record<string, any>;
  const who = name(e.actorId) || "Roomate";
  switch (e.type) {
    case "task.created":
      return `${who} created #${p.number} ${p.title}`;
    case "task.routed":
      return `routed: ${String(p.mode).replaceAll("_", " ")} → ${name(p.builderId) || "nobody"}${p.reviewerIds?.length ? `, review by ${p.reviewerIds.map(name).join(", ")}` : ""} (${p.tier})`;
    case "task.assigned":
      return `${who} assigned ${name(p.builderId) || "nobody"}${p.override ? ` — override: ${p.reason}` : " (accepted suggestion)"}`;
    case "task.claimed":
      return `${who} started on ${p.branch}`;
    case "task.stage_changed":
      return `${STAGE_LABEL[p.from as Stage] ?? p.from} → ${STAGE_LABEL[p.to as Stage] ?? p.to}${p.note ? `: ${p.note}` : ""}`;
    case "task.reverted":
      return p.sha ? `reverted on main (merge ${String(p.sha).slice(0, 7)})` : "reverted on main";
    case "task.abandoned":
      return `${who} abandoned the merge: ${p.reason ?? "no reason recorded"}`;
    case "task.note":
      return `${who}: ${p.note}`;
    case "task.no_agent_fit": {
      const areas: string[] = p.areas ?? [];
      const uncovered: string[] = p.uncovered ?? [];
      const uncoveredClause = uncovered.length ? `; nobody covers ${uncovered.join(", ")}` : "";
      return areas.length ? `no agent may build this: areas span ${areas.join(", ")}${uncoveredClause}` : `no agent may build this${uncoveredClause}`;
    }
    case "task.split_requested":
      return `too big for one task (${p.tier}, ${p.points} points); split requested${p.splitTaskNumber ? ` in #${p.splitTaskNumber}` : ""}`;
    case "task.split":
      return `split into ${(p.numbers ?? []).map((n: number) => `#${n}`).join(", ")}`;
    case "task.split_proposed":
      return "split proposed; a person must approve reworded criteria";
    case "task.split_declined":
      return `kept whole: ${p.reason ?? "no reason given"}`;
    case "overlap.detected":
      return `${who} overlaps with ${p.with?.length ?? 0} active task(s)`;
    case "provenance.recorded":
      return `${who} decided: ${p.summary} (${p.sources} source${p.sources === 1 ? "" : "s"})`;
    case "decision.created":
      return `${who} asked ${name(p.assigneeId) || "someone"}: ${p.question}`;
    case "decision.answered": {
      const detail = `“${p.optionId}”${p.rationale ? ` — ${p.rationale}` : ""} after ${formatMin(p.waitedMin)}`;
      // Derive the audit wording from the person's current name so renames cannot leave it stale.
      if (p.automated === true && !p.delegated) {
        return `${automatedAnswerAudit(name(e.actorId) || "someone")} ${detail}`;
      }
      // A delegated answer's actor is the agent, never the person it was delegated by — the one
      // label a reader must never mistake for a human's (docs/specs/review-delegation.md §4.8).
      const by = p.delegated ? `${who} (agent) · delegated by ${name(p.onBehalfOf) || "someone"}` : who;
      return `${by} answered ${detail}`;
    }
    case "decision.cancelled":
      return "decision withdrawn";
    case "evidence.submitted":
      return `${who} submitted evidence: ${p.summary}`;
    case "verification.completed":
      return `verification ${p.verdict}: ${p.why}`;
    case "intent.created":
      return `${who} opened intent “${p.title}”`;
    case "intent.submitted":
      return `${who} submitted the spec for approval`;
    case "intent.approved":
      return `${who} approved the spec`;
    case "area.penalized":
      return `${who} found an issue in a spot-check; ${p.areas?.join(", ")} reviewed more strictly for 14 days`;
    case "project.created":
      return `${who} created project ${p.name}`;
    case "actor.created":
      return `${who} added ${p.name} (${p.kind})`;
    default:
      return `${who} · ${e.type}`;
  }
}

export function useNames(actors: ActorDTO[] | undefined) {
  return (id: string | null | undefined) => (id ? (actors?.find((a) => a.id === id)?.name ?? "") : "");
}
